import { useRef } from 'react';

import { useAuth0User } from '@hooks/useAuth0User';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { useProfile } from '@hooks/useProfile';
import { isUserAuthenticated } from '@lib/utils';

const FIRST_GAMEWEEK = 1;

export type FplLinkGateStatus = 'pass' | 'loading' | 'gate' | 'error';

export type UseFplLinkGateResult = {
  status: FplLinkGateStatus;
  refetchProfile: () => void;
};

/**
 * Decides whether a page that needs a linked FPL team should show the
 * full-page link gate. Mirrors the app's useStartupGate cascade: the gate
 * applies from the gameweek 1 deadline (the first moment FPL can serve any
 * entry, since it 404s every team before then), holds (loading) while the
 * league context or profile resolve so linked users never see a flash of
 * the gate, and reports an error instead of the link prompt when the
 * profile fetch fails, so already-linked users are never asked to re-link.
 *
 * Once the status has settled (pass, gate or error) a later return to
 * 'loading' is held at the settled value. The inputs can go transiently
 * unresolved mid-session without anything having changed: useProfile keys
 * its query on the access token, which churns through a null-token key
 * whenever the Auth0 user re-hydrates, and the league context refetches on
 * window focus. Surfacing those as 'loading' swapped the page (or the gate,
 * with a half-typed FPL ID in it) for a spinner and remounted it with fresh
 * state on the way back. Only a settled status can change the rendered page.
 *
 * Maintenance mode needs no handling here: MyTeamTabsHOC replaces all
 * /my-team/* page content while isFplUpdating is true, so gated pages are
 * never rendered during an FPL update.
 */
export const useFplLinkGate = (): UseFplLinkGateResult => {
  const lastSettledStatusRef = useRef<FplLinkGateStatus | null>(null);
  const { user, isLoading: userIsLoading } = useAuth0User();
  const { context, isLoading: contextIsLoading } = useLeagueContext();
  const {
    profile,
    isError: profileHasError,
    refetch: refetchProfile
  } = useProfile();

  const fplTeamId = profile?.fplTeamId ?? null;
  const currentGameweek = context?.currentGameweek ?? null;
  const nextGameweek = context?.nextGameweek ?? null;
  const currentGameweekNumber = currentGameweek?.number ?? 0;

  // Derived from deadlineAt against the wall clock, not from the gameweek
  // number alone: FPL only flips currentGameweek once it has processed the
  // deadline (often tens of minutes late) and the context response is cached,
  // so the number would hold the gate closed after the deadline has really
  // passed. nextGameweek is checked too for the rollover gap where gameweek 1
  // has just gone live but still sits in nextGameweek.
  const gameweek1 =
    currentGameweek?.number === FIRST_GAMEWEEK
      ? currentGameweek
      : nextGameweek?.number === FIRST_GAMEWEEK
      ? nextGameweek
      : null;
  const hasGameweek1DeadlinePassed =
    currentGameweekNumber > FIRST_GAMEWEEK ||
    (gameweek1 !== null &&
      new Date(gameweek1.deadlineAt).getTime() <= Date.now());

  const getStatus = (): FplLinkGateStatus => {
    if (userIsLoading) return 'loading';
    // Pages own their logged-out handling (paywall or redirect).
    if (!isUserAuthenticated(user)) return 'pass';
    if (contextIsLoading) return 'loading';
    // Before the gameweek 1 deadline FPL cannot serve any entry, so there is
    // nothing to import and pre-season onboarding owns the no-team state.
    if (!hasGameweek1DeadlinePassed) return 'pass';
    // A present fplTeamId always passes, even if a background refetch
    // errored: a stale profile is still authoritative for linking.
    if (fplTeamId !== null) return 'pass';
    // Gate only on a resolved profile. useProfile's isLoading cannot be
    // trusted here: it is masked to false while the access token is still
    // resolving, and the query key churns through a null-token key on
    // remounts, both of which leave profile null with isLoading false and
    // would flash the gate at users whose linked state is simply unknown.
    // A resolved unlinked profile gates even if a background refetch has
    // errored: the cached data is still authoritative, and the user can
    // still link. The error state is only for a profile that never loaded.
    if (profile !== null) return 'gate';
    if (profileHasError) return 'error';
    return 'loading';
  };

  const currentStatus = getStatus();
  if (currentStatus !== 'loading') {
    lastSettledStatusRef.current = currentStatus;
  }
  const status = lastSettledStatusRef.current ?? currentStatus;

  return { status, refetchProfile };
};
